import React, { useState, useEffect, useRef } from 'react';
import { useMyContext } from '../../../context/MyContext';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import axios from 'axios';
import { toast } from 'react-toastify';
import '../../../css/custom-scrollbar.css';
import { b2bCreateWarehouse, Base_Url, getpincode, fetchUser } from '../../../config/config';
import { useNavigate } from 'react-router-dom';

// Icons
const WarehouseIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-4 h-4">
    <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z" />
    <polyline points="9,22 9,12 15,12 15,22" />
  </svg>
);

const HeavyIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-7 h-7">
    <rect x="2" y="7" width="20" height="14" rx="2" />
    <path d="M16 7V5a2 2 0 00-2-2h-4a2 2 0 00-2 2v2" />
    <line x1="12" y1="12" x2="12" y2="16" />
    <line x1="10" y1="14" x2="14" y2="14" />
  </svg>
);

const LiteIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-7 h-7">
    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
  </svg>
);

const DenseIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-7 h-7">
    <rect x="3" y="3" width="7" height="7" />
    <rect x="14" y="3" width="7" height="7" />
    <rect x="14" y="14" width="7" height="7" />
    <rect x="3" y="14" width="7" height="7" />
  </svg>
);

const CloseIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-5 h-5">
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

const WAREHOUSE_TYPES = [
  {
    id: 'lite',
    label: 'Lite',
    sublabel: '',
    description: '',
    color: 'black',
    icon: <LiteIcon />,
    gradient: 'from-gray-50 to-gray-100',
    border: 'border-green-600',
    badge: 'bg-green-400 opacity-90 text-slate-800',
    dot: 'bg-white',
  },
  {
    id: 'heavy',
    label: 'Heavy',
    sublabel: '',
    description: 'Bulk shipments & heavy cargo',
    color: 'black',
    icon: <HeavyIcon />,
    gradient: 'from-gray-50 to-gray-100',
    border: 'border-amber-600',
    badge: 'bg-yellow-400 opacity-90 text-slate-800',
    dot: 'bg-white',
  },
  {
    id: 'dense',
    label: 'Dense',
    sublabel: '',
    description: 'High-density stacked storage',
    color: 'black',
    icon: <DenseIcon />,
    gradient: 'from-gray-50 to-gray-100',
    border: 'border-pink-600',
    badge: 'bg-rose-400 opacity-90 text-slate-800',
    ring: 'ring-black',
    dot: 'bg-white',
  },
];

const schema = z.object({
  facilityName: z.string()
    .min(1, "Facility name is required")
    .regex(/^[A-Za-z0-9\s]+$/, "Facility name must only contain letters, numbers, and spaces"),
  contactPerson: z.string()
    .min(1, "Contact Person is required")
    .regex(/^[A-Za-z0-9\s-]+$/, "Contact Person must only contain letters, numbers, spaces, and hyphens"),
  phone: z.string()
    .length(10, "Phone number must be exactly 10 digits")
    .regex(/^\d+$/, "Phone number must be numeric"),
  pincode: z.string()
    .min(6, "Pincode should be exactly 6 characters")
    .max(6, "Pincode should be exactly 6 characters")
    .regex(/^\d+$/, "Pincode must be numeric"),
  city: z.string().min(1, "City is required").regex(/^[a-zA-Z\s]+$/, "City must only contain alphabetic characters"),
  state: z.string().min(1, "State is required").regex(/^[a-zA-Z\s]+$/, "State must only contain alphabetic characters"),
  email: z.string().optional(),
  address: z.string()
    .min(1, "Address is required")
    .regex(/^[^#\\%&;]*$/, "Address contains invalid characters (# \\ % & ; not allowed)"),
  returnAddress: z.string().optional(),
  returnPincode: z.string().optional(),
});

// Reusable field component — compact
const Field = ({ label, id, error, isDarkMode, children }) => (
  <div className="flex flex-col gap-0.5">
    <label htmlFor={id} className={`text-[9px] font-semibold tracking-wide uppercase ${isDarkMode ? 'text-indigo-200/80' : 'text-gray-500'}`}>
      {label}
    </label>
    {children}
    {error && (
      <p className="text-red-400 text-[9px] leading-tight flex items-center gap-1">
        <span>⚠</span> {error.message}
      </p>
    )}
  </div>
);

const inputClass = (hasError, isDarkMode) =>
  `w-full px-2 py-1.5 text-[11px] rounded-md border transition-all focus:outline-none focus:ring-1
  ${hasError
    ? 'border-red-500/70 bg-red-500/[0.08] dark:text-white'
    : isDarkMode
      ? 'border-indigo-400/50 bg-[#1a1a2e] text-white placeholder-indigo-300/50 focus:ring-indigo-500/40 focus:border-indigo-400 dark:shadow-[inset_0_1px_0_rgba(255,255,255,0.05)]'
      : 'border-gray-300 bg-white text-black placeholder-gray-400 focus:ring-gray-400 focus:border-gray-400'
  }`;

// Searchable Dropdown Component
const SearchableDropdown = ({ options, value, onChange, placeholder, isDarkMode, error }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef(null);

  const selectedOption = options.find(opt => opt.value === value);
  const displayValue = selectedOption ? selectedOption.label : '';

  const filteredOptions = options.filter(opt =>
    opt.label.toLowerCase().includes(searchTerm.toLowerCase())
  );

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (!isOpen) {
      setSearchTerm('');
    }
  }, [isOpen]);

  const handleSelect = (option) => {
    onChange(option.value);
    setIsOpen(false);
    setSearchTerm('');
  };

  return (
    <div className="relative w-full" ref={dropdownRef}>
      <div
        className={`w-full px-2 py-1.5 text-[11px] rounded-md border transition-all duration-150 cursor-pointer flex items-center justify-between
          ${error
            ? 'border-red-500/70 bg-red-500/[0.08]'
            : isDarkMode
              ? 'border-indigo-400/50 bg-[#1a1a2e] text-white dark:shadow-[inset_0_1px_0_rgba(255,255,255,0.05)]'
              : 'border-gray-300 bg-white text-black'
          }
          focus-within:ring-1 ${isDarkMode ? 'focus-within:ring-indigo-500/40 focus-within:border-indigo-400' : 'focus-within:ring-black focus-within:border-black'}`}
        onClick={() => setIsOpen(!isOpen)}
      >
        <input
          type="text"
          placeholder={placeholder}
          value={isOpen ? searchTerm : displayValue}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            if (!isOpen) setIsOpen(true);
          }}
          onFocus={() => setIsOpen(true)}
          className={`w-full bg-transparent outline-none text-[11px] ${isDarkMode ? 'text-white placeholder-indigo-300/50' : 'text-black placeholder-gray-400'}`}
          readOnly={!isOpen}
        />
        <svg
          className={`w-4 h-4 transition-transform duration-200 flex-shrink-0 ${isOpen ? 'rotate-180' : ''} ${isDarkMode ? 'text-indigo-300' : 'text-gray-500'}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
        </svg>
      </div>

      {isOpen && (
        <div
          className={`absolute z-50 w-full mt-1 rounded-md border shadow-lg max-h-48 overflow-y-auto
          ${isDarkMode
            ? 'bg-gradient-to-br from-[#0b0b14] via-[#0d0d18] to-[#0b0b14] border-indigo-500/40 shadow-[0_8px_40px_rgba(79,70,229,0.35)]'
            : 'bg-white border-gray-200'
          }`}
          style={{ top: '100%' }}
        >
          {filteredOptions.length > 0 ? (
            filteredOptions.map((option) => (
              <div
                key={option.value}
                className={`px-3 py-1.5 text-[11px] cursor-pointer transition-colors
                  ${option.value === value
                    ? isDarkMode ? 'bg-indigo-500/25 text-white' : 'bg-gray-100 text-black'
                    : isDarkMode ? 'text-indigo-100 hover:bg-indigo-500/15' : 'text-black hover:bg-gray-50'
                  }`}
                onClick={() => handleSelect(option)}
              >
                {option.label}
              </div>
            ))
          ) : (
            <div className={`px-3 py-1.5 text-[11px] ${isDarkMode ? 'text-indigo-300/60' : 'text-gray-500'}`}>
              No retailers found
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ⬇️ IMPORTANT: ab ye component 'isOpen' aur 'onClose' props leta hai
const AddWarehouse = ({ isOpen, onClose, onCreated }) => {
  const { isDarkMode } = useMyContext();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [retailerId, setRetailerId] = useState('');
  const [retailerIdData, setRetailerIdData] = useState([]);
  const [selectedWarehouseType, setSelectedWarehouseType] = useState('lite');
  const role = localStorage.getItem("role");
  const navigate = useNavigate();

  const { register, handleSubmit, formState: { errors }, setValue, reset } = useForm({
    resolver: zodResolver(schema),
  });

  useEffect(() => {
    if (isOpen) fetchRetailer();
  }, [isOpen]);

  const handleInputChange = (event) => {
    const { value, id } = event.target;
    if (['pincode', 'phone', 'return-pincode'].includes(id)) {
      const filtered = value.replace(/[^0-9]/g, '');
      event.target.value = filtered;
      if (id === 'pincode' && filtered.length === 6) fetchPincodeData(filtered);
      if (id === 'return-pincode' && filtered.length === 6) fetchReturnPincodeData(filtered);
    }
    if (['city', 'state', 'returnState', 'returnCity', 'country'].includes(id)) {
      event.target.value = value.replace(/[^a-zA-Z\s]/g, '');
    }
  };

  const fetchPincodeData = async (pincode) => {
    const token = localStorage.getItem('token');
    const cus_id = localStorage.getItem('cus_id');
    try {
      const response = await fetch(`${Base_Url}${getpincode}?pincode=${pincode}`, {
        headers: { token, cus_id, 'Content-Type': 'application/json' },
      });
      const data = await response.json();
      if (data?.status === "SUCCESS") {
        let { city, state } = data.data[0];
        setValue("city", city.charAt(0).toUpperCase() + city.slice(1).toLowerCase());
        setValue("state", state.charAt(0).toUpperCase() + state.slice(1).toLowerCase());
      } else if (data?.status === "FAIL") {
        toast.error("Invalid pincode, please try another.");
      }
    } catch { toast.error("Error fetching pincode data"); }
  };

  const fetchReturnPincodeData = async (returnPincode) => {
    const token = localStorage.getItem('token');
    const cus_id = localStorage.getItem('cus_id');
    try {
      const response = await fetch(`${Base_Url}${getpincode}?pincode=${returnPincode}`, {
        headers: { token, cus_id, 'Content-Type': 'application/json' },
      });
      const data = await response.json();
      if (data?.status === "SUCCESS") {
        let { city, state } = data.data[0];
        setValue("returnCity", city.charAt(0).toUpperCase() + city.slice(1).toLowerCase());
        setValue("returnState", state.charAt(0).toUpperCase() + state.slice(1).toLowerCase());
      } else if (data?.status === "FAIL") {
        toast.error("Invalid return pincode.");
      }
    } catch { toast.error("Error fetching return pincode data"); }
  };

  const fetchRetailer = async () => {
    const token = localStorage.getItem('token');
    const cusid = localStorage.getItem('cus_id');
    try {
      const response = await fetch(Base_Url + fetchUser, {
        method: 'GET',
        headers: { token, cus_id: cusid, 'Content-Type': 'application/json' },
      });
      const data = await response.json();
      setRetailerIdData(data.data.map(r => ({ value: r.id, label: r.name })));
    } catch (error) { console.error('Error fetching retailers:', error); }
  };

  const onSubmit = async (data, event) => {
    event.preventDefault();
    const token = localStorage.getItem('token');
    const adminId = localStorage.getItem('cus_id');
    setIsSubmitting(true);

    const payload = {
      pin_code: data.pincode,
      city: data.city,
      state: data.state,
      country: "India",
      cft_type: selectedWarehouseType,
      address_details: {
        address: data.address,
        contact_person: data.contactPerson,
        phone_number: data.phone,
      },
      name: data.facilityName,
      ret_address: {
        pin: data.returnPincode,
        address: data.returnAddress,
      },
    };

    try {
      const response = await axios.post(Base_Url + b2bCreateWarehouse, payload, {
        headers: { token, cus_id: adminId, 'Content-Type': 'application/json' },
      });

      if (response?.data?.status === "SUCCESS") {
        toast.success(response.data.message);
        setTimeout(() => {
          reset();
          onCreated?.();
          onClose?.();
        }, 1000);
      } else {
        const m = response.data.messages || {};
        toast.error(m.pin_code || m.country || m.name || m["ret_address.pin"] || m["ret_address.address"] || m["address_details.phone_number"] || "Something went wrong.");
      }
    } catch (error) {
      const m = error?.response?.data?.messages || {};
      toast.error(m.pin_code || m.country || m.name || m["ret_address.pin"] || m["ret_address.address"] || m["address_details.phone_number"] || "Something went wrong.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const activeType = WAREHOUSE_TYPES.find(t => t.id === selectedWarehouseType);

  return (
    <>
      {/* Overlay */}
      <div
        onClick={onClose}
        className={`fixed inset-0 bg-black/40 dark:bg-black/75 backdrop-blur-[2px] dark:backdrop-blur-md z-[100] transition-opacity duration-300
          ${isOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}`}
      />

      {/* Left Side Sliding Panel */}
      <div
        className={`fixed top-0 right-0 h-full z-[101] w-[50%] sm:w-[60%] md:w-[60%] max-w-[40%]
          bg-white dark:bg-gradient-to-br dark:from-[#0b0b14] dark:via-[#0d0d18] dark:to-[#0b0b14]
          shadow-2xl dark:shadow-[0_0_60px_rgba(79,70,229,0.35)]
          rounded-tl-xl rounded-bl-lg transform transition-transform duration-300 ease-in-out
          overflow-y-auto custom-scrollbar
          ${isOpen ? 'translate-x-0' : 'translate-x-full'}
          ${isDarkMode ? 'border-l border-indigo-500/30' : ''}`}
      >
        <div className="px-2 md:px-6 py-2 min-h-full flex flex-col">

          {/* Header + Close */}
          <div className="flex items-center justify-between mb-2 flex-shrink-0">
            <div className="flex items-center gap-2">
              <div className="p-1.5 bg-red-600 dark:bg-gradient-to-br dark:from-indigo-600 dark:to-purple-600 dark:shadow-lg dark:shadow-indigo-900/50 rounded-md text-white">
                <WarehouseIcon color="white" />
              </div>
              <div>
                <h1 className={`text-[13px] font-bold leading-tight uppercase tracking-tight ${isDarkMode ? 'text-white' : 'text-black'}`}>
                  Add New Pickup Location
                </h1>
                <p className={`text-[9px] leading-tight ${isDarkMode ? 'text-indigo-200/60' : 'text-gray-500'}`}>
                  Configure your warehouse details and type
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={onClose}
              className={`p-1.5 rounded-md transition ${isDarkMode ? 'text-indigo-300/70 hover:bg-indigo-500/15 hover:text-white' : 'text-gray-500 hover:bg-gray-100'}`}
            >
              <CloseIcon />
            </button>
          </div>

          <form onSubmit={handleSubmit(onSubmit, () => toast.error("Please fix the errors in the form."))}
            className="flex flex-col gap-2 min-h-0">

            {/* ── Warehouse Type Selector ── */}
            <div className={`rounded-lg border px-3 py-2 flex flex-col sm:flex-row sm:items-center gap-2 flex-shrink-0 ${
              isDarkMode
                ? 'bg-gradient-to-br from-[#0b0b14] via-[#0d0d18] to-[#0b0b14] border-indigo-500/25 shadow-[0_4px_20px_rgba(79,70,229,0.12)]'
                : 'bg-white border-gray-200'
            }`}>
              <div className="flex-shrink-0">
                <p className={`text-[11px] font-semibold leading-tight uppercase tracking-wide ${isDarkMode ? 'text-white' : 'text-black'}`}>
                  Warehouse Type
                </p>
                <p className={`text-[9px] leading-tight ${isDarkMode ? 'text-indigo-200/60' : 'text-gray-400'}`}>
                  Select storage capacity
                </p>
              </div>

              <div className={`hidden sm:block w-px self-stretch ${isDarkMode ? 'bg-indigo-500/25' : 'bg-gray-200'}`} />

              <div className="flex flex-1 gap-1.5 flex-wrap">
                {WAREHOUSE_TYPES.map((type) => {
                  const isActive = selectedWarehouseType === type.id;
                  return (
                    <button
                      key={type.id}
                      type="button"
                      onClick={() => setSelectedWarehouseType(type.id)}
                      className={`
                        flex items-center gap-1.5 px-2.5 py-1 rounded-md border text-[11px] font-medium transition-all duration-150 cursor-pointer
                        ${isActive
                          ? `${type.border} ${type.badge} border-2`
                          : isDarkMode
                            ? 'border-indigo-500/30 bg-indigo-500/[0.06] text-indigo-200 hover:border-indigo-400 hover:bg-indigo-500/15'
                            : 'border-gray-200 bg-white text-gray-500 hover:bg-gray-50 hover:border-black'
                        }
                      `}
                    >
                      {isActive && <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${type.dot}`} />}
                      <span>{type.label}</span>
                      <span className={`text-[9px] font-normal ${isActive ? 'opacity-80' : 'opacity-50'}`}>{type.sublabel}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* ── Facility Details ── */}
            <div className={`rounded-lg border p-3 flex-shrink-0 ${
              isDarkMode
                ? 'bg-gradient-to-br from-[#0b0b14] via-[#0d0d18] to-[#0b0b14] border-indigo-500/25 shadow-[0_4px_20px_rgba(79,70,229,0.12)]'
                : 'bg-white border-gray-200'
            }`}>
              <p className={`text-[11px] font-semibold mb-2 uppercase tracking-wide ${isDarkMode ? 'text-white' : 'text-black'}`}>
                Facility Details
              </p>

              <div className="grid grid-cols-2 md:grid-cols-2 gap-2.5">

                {role === 'admin' && (
                  <Field label="Select Retailer" id="retailer" isDarkMode={isDarkMode}>
                    <SearchableDropdown
                      options={retailerIdData}
                      value={retailerId}
                      onChange={setRetailerId}
                      placeholder="Search retailer..."
                      isDarkMode={isDarkMode}
                      error={false}
                    />
                  </Field>
                )}

                <Field label="Facility Name" id="facility-name" error={errors.facilityName} isDarkMode={isDarkMode}>
                  <input id="facility-name" type="text" placeholder="e.g. North Delhi Hub"
                    className={inputClass(errors.facilityName, isDarkMode)}
                    {...register('facilityName')} onChange={handleInputChange} />
                </Field>

                <Field label="Contact Person" id="contact-person" error={errors.contactPerson} isDarkMode={isDarkMode}>
                  <input id="contact-person" type="text" placeholder="Full name"
                    className={inputClass(errors.contactPerson, isDarkMode)}
                    {...register('contactPerson')} onChange={handleInputChange} />
                </Field>

                <Field label="Phone Number" id="phone" error={errors.phone} isDarkMode={isDarkMode}>
                  <input id="phone" type="tel" maxLength="10" placeholder="10-digit mobile number"
                    className={inputClass(errors.phone, isDarkMode)}
                    {...register('phone')} onChange={handleInputChange} />
                </Field>

                <Field label="Pincode" id="pincode" error={errors.pincode} isDarkMode={isDarkMode}>
                  <input id="pincode" type="text" maxLength="6" placeholder="6-digit pincode"
                    className={inputClass(errors.pincode, isDarkMode)}
                    {...register('pincode')} onChange={handleInputChange} />
                </Field>

                <Field label="City" id="city" error={errors.city} isDarkMode={isDarkMode}>
                  <input id="city" type="text" placeholder="Auto-filled"
                    className={`${inputClass(errors.city, isDarkMode)} disabled:opacity-60 cursor-not-allowed`}
                    {...register('city')} disabled onChange={handleInputChange} />
                </Field>

                <Field label="State" id="state" error={errors.state} isDarkMode={isDarkMode}>
                  <input id="state" type="text" placeholder="Auto-filled"
                    className={`${inputClass(errors.state, isDarkMode)} disabled:opacity-60 cursor-not-allowed`}
                    {...register('state')} disabled onChange={handleInputChange} />
                </Field>

                <Field label="Country" id="country" isDarkMode={isDarkMode}>
                  <input id="country" type="text" value="India" disabled
                    className={`${inputClass(false, isDarkMode)} disabled:opacity-60 cursor-not-allowed`} />
                </Field>

               <div className="col-span-2">
                 <Field label="Address" id="address" error={errors.address} isDarkMode={isDarkMode}>
                  <input id="address" type="text" placeholder="Building, street, area"
                    className={inputClass(errors.address, isDarkMode)}
                    {...register('address')} />
                </Field>
               </div>

              </div>
            </div>

            {/* ── Return Address ── */}
            <div className={`rounded-lg border p-3 flex-shrink-0 ${
              isDarkMode
                ? 'bg-gradient-to-br from-[#0b0b14] via-[#0d0d18] to-[#0b0b14] border-indigo-500/25 shadow-[0_4px_20px_rgba(79,70,229,0.12)]'
                : 'bg-white border-gray-200'
            }`}>
              <div className="flex items-baseline gap-1.5 mb-2">
                <p className={`text-[11px] font-semibold uppercase tracking-wide ${isDarkMode ? 'text-white' : 'text-black'}`}>
                  Return Address
                </p>
                <span className={`text-[9px] font-normal ${isDarkMode ? 'text-indigo-300/60' : 'text-gray-400'}`}>
                  (optional — leave blank to use the same address for returns)
                </span>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5">
                <Field label="Return Pincode" id="return-pincode" isDarkMode={isDarkMode}>
                  <input id="return-pincode" type="text" maxLength="6" placeholder="6-digit return pincode"
                    className={inputClass(false, isDarkMode)}
                    {...register('returnPincode')} onChange={handleInputChange} />
                </Field>

                <Field label="Return Address" id="returnAddress" isDarkMode={isDarkMode}>
                  <input id="returnAddress" type="text" placeholder="Return location address"
                    className={inputClass(false, isDarkMode)}
                    {...register('returnAddress')} />
                </Field>
              </div>
            </div>

            {/* ── Submit ── */}
            <div className="flex items-center justify-end gap-2 mt-2 pb-1">
              <button
                type="button"
                onClick={onClose}
                className={`px-4 py-1.5 rounded-md text-[11px] font-medium border transition ${
                  isDarkMode
                    ? 'border-indigo-500/40 text-indigo-200 hover:bg-indigo-500/15'
                    : 'border-gray-300 bg-gray-300 text-black hover:bg-gray-50'
                }`}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className={`flex-1 md:flex-none md:min-w-[180px] flex items-center justify-center gap-1.5 px-5 py-1.5 rounded-md text-[11px] font-semibold uppercase tracking-wide text-white transition-all duration-200
                  ${isSubmitting
                    ? isDarkMode
                      ? 'bg-indigo-500/15 text-indigo-300/50 cursor-not-allowed'
                      : 'bg-gray-400 cursor-not-allowed'
                    : isDarkMode
                      ? 'bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-500 hover:to-purple-500 shadow-lg shadow-indigo-900/50 active:scale-95'
                      : 'bg-red-600 hover:bg-slate-700 active:scale-95'
                  }`}
              >
                {isSubmitting ? (
                  <>
                    <svg className="animate-spin w-3.5 h-3.5" viewBox="0 0 24 24" fill="none">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                    </svg>
                    Creating Warehouse...
                  </>
                ) : (
                  <>
                    <WarehouseIcon />
                    Create {activeType?.label} Warehouse
                  </>
                )}
              </button>
            </div>

          </form>
        </div>
      </div>
    </>
  );
};

export default AddWarehouse;